import type { GenerationOptions } from 'text-to-svg';

import { type Point, Rectangle, rectangle } from '../../elements';
import type { NodeData, NodeId } from '../../nodes';
import { NODE_LABEL_FONT_SIZE } from '../../nodes/constants';
import { nodeIconDefaultOptions } from '../../nodes/icons/NodeTypeIcon';
import { nodeTypeIcons } from '../../nodes/icons/icons';
import { anchorToSvgText, getGraphFonts } from '../../svg/text';
import type { GraphExportData } from './GraphExportData';
import { SVG_HIDDEN_ELEMENT_OPACITY } from './style';

const renderNodeIcon = (node: NodeData, hidden: boolean) =>
  nodeTypeIcons[node.type].createIconSvg({
    style: node.style ?? nodeIconDefaultOptions.style,
    attributes: {
      ...getNodeTopLeft(node),
      ...(hidden && { opacity: SVG_HIDDEN_ELEMENT_OPACITY }),
    },
  });

const getNodeTopLeft = (node: NodeData): Point => {
  const { width, height } = nodeTypeIcons[node.type].getSize(node.style);
  return {
    x: node.position.x - width / 2,
    y: node.position.y - height / 2,
  };
};

const getNodeLabelOptions = (node: NodeData): GenerationOptions => {
  const labelStyle = nodeTypeIcons[node.type].getLabelStyle(node.style);
  return {
    x: node.position.x + labelStyle.offset.x,
    y: node.position.y + labelStyle.offset.y,
    fontSize: NODE_LABEL_FONT_SIZE,
    anchor: anchorToSvgText(labelStyle.anchor),
    attributes: { fill: 'black' },
  };
};

const renderNode =
  (hiddenNodes: Set<NodeId> | undefined) =>
  (node: NodeData): [svg: string, boundingBox: Rectangle] => {
    const nodeTopLeft = getNodeTopLeft(node);
    const nodeSize = nodeTypeIcons[node.type].getSize(node.style);

    const icon = renderNodeIcon(node, !!hiddenNodes?.has(node.id));

    const svgFont = getGraphFonts().nodeLabels;
    const labelOptions = getNodeLabelOptions(node);
    const label = svgFont.getPath(node.label, labelOptions);
    const labelMetrics = svgFont.getMetrics(node.label, labelOptions);

    const boundingBox = rectangle.mergeBoundingBoxes(
      new Rectangle(labelMetrics, labelMetrics.width, labelMetrics.height),
      new Rectangle(nodeTopLeft, nodeSize.width, nodeSize.height),
    );

    return [`${icon}${label}`, boundingBox];
  };

export const renderNodes = (
  graph: GraphExportData,
): [svg: string, boundingBox: Rectangle | null] => {
  const nodeInstances = Object.values(graph.nodes).map(
    renderNode(graph.hiddenNodes),
  );

  const nodeInstancesSvg = nodeInstances.map(([svg]) => svg).join('');
  const nodeBBs =
    nodeInstances.length > 0
      ? rectangle.mergeBoundingBoxes(...nodeInstances.map(([, bb]) => bb))
      : null;

  return [nodeInstancesSvg, nodeBBs];
};
